import React, { useState } from 'react';
import { changePassword, ChangePasswordRequest } from '../../services/userService';
import { FaEye, FaEyeSlash, FaLock } from 'react-icons/fa';
import './passwordModal.css';

interface PasswordModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const PasswordModal: React.FC<PasswordModalProps> = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState<ChangePasswordRequest>({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordMessage, setPasswordMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  if (!isOpen) return null;

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  const toggleCurrentPasswordVisibility = () => {
    setShowCurrentPassword(prev => !prev);
  };
  
  const toggleNewPasswordVisibility = () => {
    setShowNewPassword(prev => !prev);
  };
  
  const toggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword(prev => !prev);
  };

  const validateForm = (): boolean => {
    // Kiểm tra mật khẩu hiện tại
    if (!formData.currentPassword) {
      setPasswordMessage('Vui lòng nhập mật khẩu hiện tại');
      return false;
    }
    
    // Kiểm tra mật khẩu mới
    if (!formData.newPassword) {
      setPasswordMessage('Vui lòng nhập mật khẩu mới');
      return false;
    }
    
    if (formData.newPassword.length < 6) {
      setPasswordMessage('Mật khẩu mới phải có ít nhất 6 ký tự');
      return false;
    }
    
    // Kiểm tra xác nhận mật khẩu
    if (formData.newPassword !== formData.confirmPassword) {
      setPasswordMessage('Xác nhận mật khẩu không khớp');
      return false;
    }
    
    return true;
  };

  const handleChangePassword = async () => {
    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    setPasswordMessage('');
    
    try {
      const response = await changePassword(formData);
      
      if (response.result === 'SUCCESS') {
        setIsSuccess(true);
        setPasswordMessage('Đổi mật khẩu thành công');
        // Reset form
        setFormData({
          currentPassword: '',
          newPassword: '',
          confirmPassword: ''
        });
        
        // Đóng modal sau 2 giây
        setTimeout(() => {
          onClose();
        }, 2000);
      } else {
        setIsSuccess(false);
        setPasswordMessage(response.message || 'Có lỗi xảy ra khi đổi mật khẩu');
      }
    } catch (error: any) {
      setIsSuccess(false);
      setPasswordMessage(error.message || 'Có lỗi xảy ra khi đổi mật khẩu');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    setFormData({
      currentPassword: '',
      newPassword: '',
      confirmPassword: ''
    });
    setPasswordMessage('');
    onClose();
  };

  return (
    <div className="modal-overlay">
      <div className="modal-container">
        <h2 className="modal-title">
          <FaLock style={{ marginRight: '10px' }} /> ĐỔI MẬT KHẨU
        </h2>
        
        <div className="form-group">
          <label htmlFor="currentPassword">Mật khẩu hiện tại:</label>
          <div className="password-input-wrapper">
            <input 
              type={showCurrentPassword ? "text" : "password"} 
              id="currentPassword"
              name="currentPassword"
              value={formData.currentPassword}
              onChange={handleChange}
              placeholder="Nhập mật khẩu hiện tại"
              autoComplete="current-password"
            />
            <button 
              type="button" 
              className="password-toggle-btn"
              onClick={toggleCurrentPasswordVisibility}
              aria-label={showCurrentPassword ? "Ẩn mật khẩu" : "Hiện mật khẩu"}
            >
              {showCurrentPassword ? <FaEyeSlash /> : <FaEye />}
            </button>
          </div>
        </div>
        
        <div className="form-group">
          <label htmlFor="newPassword">Mật khẩu mới:</label>
          <div className="password-input-wrapper">
            <input 
              type={showNewPassword ? "text" : "password"} 
              id="newPassword"
              name="newPassword"
              value={formData.newPassword}
              onChange={handleChange}
              placeholder="Nhập mật khẩu mới (ít nhất 6 ký tự)"
              autoComplete="new-password"
            />
            <button 
              type="button" 
              className="password-toggle-btn"
              onClick={toggleNewPasswordVisibility}
              aria-label={showNewPassword ? "Ẩn mật khẩu" : "Hiện mật khẩu"}
            >
              {showNewPassword ? <FaEyeSlash /> : <FaEye />}
            </button>
          </div>
        </div>
        
        <div className="form-group">
          <label htmlFor="confirmPassword">Xác nhận mật khẩu mới:</label>
          <div className="password-input-wrapper">
            <input 
              type="password"
              id="confirmPassword"
              name="confirmPassword"
              value={formData.confirmPassword}
              onChange={handleChange}
              placeholder="Nhập lại mật khẩu mới"
              autoComplete="new-password"
            />
          </div>
        </div>
        
        <div className="form-group message-container">
          <div></div>
          {passwordMessage && (
            <div className={`message-box ${isSuccess ? 'success' : 'error'}`}>
              {passwordMessage}
            </div>
          )}
        </div>
        
        <div className="form-group actions-container">
          <div></div>
          <div className="modal-actions">
            <button 
              onClick={handleCancel}
              className="btn btn-secondary"
              type="button"
            >
              Hủy bỏ
            </button>
            
            <button 
              onClick={handleChangePassword}
              disabled={isLoading}
              className="btn btn-primary"
              type="button"
            >
              {isLoading ? 'Đang xử lý...' : 'Xác nhận'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PasswordModal; 